import moment from "moment";
import lodash from "lodash";
import { UseQueryStateResult } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { FILTERING_OPERATORS } from "./constants";

const DESCENDING = "desc";
const ASCENDING = "asc";
const HASH = "#";

/**
 * Function to remove items from the array
 * @param array
 * @param items
 * @returns filtered array
 */
export const removeItemsFromArray = (array: any[], items: any[]) => {
  items.forEach((item: any) => {
    const index = array.indexOf(item);
    if (index > -1) {
      array.splice(index, 1);
    }
  });
  return array;
};

/**
 * Function to convert an array into an array of object
 * Such is a requirement to convert the arrays for displaying Select Boxes
 * @param objectKeys array of keys within the object like ["key1", "key2"]
 * @param array any array like ["a","b","c"]
 * @returns an array of object collated for example [{key1:a,key2:a},{key1:b,key2:b},{key1:c, key2:c}]
 */
export const convertArrayToArrayOfObject = (
  objectKeys: string[],
  array: any[]
) => {
  return array.map((item) => {
    var obj: any = {};
    objectKeys.forEach((key) => (obj[key] = item));
    return obj;
  });
};

/**
 * Function to convert text from camelCase to TitelCase
 * Example : charAt -> Char At
 * @param text
 * @returns converted text
 */
export const convertCamelCaseToTitleCase = (text: string) => {
  const result = text.replace(/([A-Z])/g, " $1");
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

/**
 * Function to format the date in the expected format
 * @param date
 * @param currentFormant
 * @param newFormat
 * @returns string with the new date format
 */
export const getFormattedDate = (
  date: moment.MomentInput,
  currentFormant: string,
  newFormat: string
) => {
  return moment(date, currentFormant).format(newFormat);
};
/*
 * Function To Sort the Data
 * @param records
 * @param sortingColumn
 * @param isDescending
 * @param floatSortColumns
 * @returns
 */
export const sortRecords = (
  records: any[],
  sortingColumn: string | null,
  isDescending: boolean,
  floatSortColumns: string[] = []
) => {
  let sortedRecords = records;
  if (!sortingColumn) {
    return records;
  }
  sortedRecords = lodash.orderBy(
    sortedRecords,
    floatSortColumns.includes(sortingColumn)
      ? (record) => {
          return parseFloat(record[sortingColumn]);
        }
      : sortingColumn,
    isDescending ? DESCENDING : ASCENDING
  );
  return sortedRecords;
};

/**
 * Function to get filtering properties
 *
 * @param tableColumns Columns of the Table to get Filtering Properties
 * @param filteringOperators Operators for the filtering
 * @returns {propertyLabel, key, groupValuesLabel, operators}[]
 */
export const getPolarisTableFilteringProperties = (
  tableColumns: any[],
  filteringOperators?: string[]
) =>
  tableColumns.map((column: string) => ({
    propertyLabel: convertCamelCaseToTitleCase(column),
    key: column,
    groupValuesLabel: convertCamelCaseToTitleCase(column),
    operators: filteringOperators ?? FILTERING_OPERATORS,
  }));

/**
 * Function to test weather a string is satisfying with the regex or not
 *
 * @param regex Regex
 * @param value String
 * @returns Boolean value denoting string is satisfying with the regex or not
 */
export const testRegex = (regex: RegExp, value: string) => {
  return regex.test(value);
};

/**
 * Function to create Object of Label and Value
 *
 * @param value value in the object
 * @param label label label in the object
 * @returns object
 */
export const createLabelValueObject = (value: string, label?: string) => {
  return {
    label: label ?? value,
    value: value,
  };
};

/**
 * Function to get the status type for the Polaris Select Box
 * @param response - fetchbasequery API response
 * @returns string
 */
export const getSelectBoxStatusType = (
  response: UseQueryStateResult<any, any>
) => {
  return response.isLoading
    ? "loading"
    : response.isError
    ? "error"
    : "finished";
};

/**
 * Function to generate the hash key for any given object
 * @param object - object for which hash key is to be generated
 * @returns - hash value of the object
 */
export const generateHashKey = <T>(object: T): string => {
  const extractValues = (input: unknown): string[] => {
    if (input === null || input === undefined) return [""];
    if (typeof input !== "object") return [String(input)];

    try {
      if (Array.isArray(input)) {
        return input.flatMap(extractValues);
      }

      return Object.keys(input)
        .sort()
        .flatMap((key) =>
          extractValues((input as Record<string, unknown>)[key])
        );
    } catch {
      return [];
    }
  };

  return extractValues(object).join(HASH);
};

/**
 * Function to get the S3 URI from the presigned URL
 * @param presignedUrl - presigned URL
 * @returns - S3 URI
 */
export const getS3UriFromPresignedUrl = (presignedUrl: string): string => {
  const url = new URL(presignedUrl);
  const bucketName = url.hostname.split(".")[0];
  const key = decodeURIComponent(url.pathname.slice(1));
  return `s3://${bucketName}/${key}`;
};

/**
 * Function to parse NDJSON stream and return an array of objects
 * @param stream - NDJSON stream
 * @returns - Promise that resolves to an array of objects
 */
export async function parseNdjsonStream<T = any>(
  stream: ReadableStream<Uint8Array>
): Promise<T[]> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");

  let buffer = "";
  const result: T[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (line.trim()) {
        try {
          result.push(JSON.parse(line));
        } catch (err) {
          console.error("Invalid JSON line:", line, err);
        }
      }
    }
  }

  if (buffer.trim()) {
    try {
      result.push(JSON.parse(buffer));
    } catch (err) {
      console.error("Invalid final JSON line:", buffer, err);
    }
  }

  return result;
}
